/**
 * Introducing two players, and then getting out of the way.
 *
 * A world hosted in somebody's browser needs one thing from us that it
 * cannot do itself: being found. Two computers behind two routers cannot
 * call each other out of nowhere - each has to be told where the other is,
 * and they have to be told it by a machine both of them can already reach.
 * That is all this is. Our server holds the introduction; the game itself
 * goes straight between them and we never see a block of it.
 *
 * **We do not read what we pass on.** An offer and a candidate are strings
 * from one player to another. Parsing them would mean our server having an
 * opinion about WebRTC, which is a pile of code and a pile of ways to be
 * wrong about a standard that changes. They are relayed as-is, with a length
 * on them, and that is the whole of our involvement.
 *
 * **What we do have an opinion about is who may say what to whom.** Three
 * rules, and they are the reason this is a class and not a Map:
 *
 *   - an offer for a world goes to that world's host and to nobody else;
 *   - a host may answer only somebody who is actually waiting on a world it
 *     is actually hosting - otherwise anybody who guessed a number could
 *     answer a stranger's offer and be their server;
 *   - everything has a ceiling: worlds per person, worlds in all, people
 *     waiting per world, and bytes per message.
 *
 * **Ratings never come from here.** A world on this list runs on somebody
 * else's machine and its results are whatever that machine says they are.
 * Only worlds we host ourselves count for anything - see the note on
 * `Host.note`. That is a decision, not an omission.
 *
 * Pure: no sockets, no timers of its own. It is told the time.
 */
import type { Phrase } from "../text/lang.js";

/**
 * What a world on somebody's machine is called.
 *
 * **Derived from whoever holds it, and from nothing else.** That is what
 * makes it the same name tomorrow: a world that came back under a new name
 * each evening would be a new world to everybody looking for it - their
 * name-tags in it filed under the old one, their bags with them, and the
 * link somebody was given leading nowhere. It also means one account holds
 * one world, which is the cap, enforced by the arithmetic rather than by a
 * count that has to be got right.
 *
 * Encoded rather than used raw: a name may hold anything printable, and
 * `|` and `/` are separators elsewhere in this game - in the key a browser
 * files its name-tags under, and in the path a browser keeps a world at.
 *
 * It is not a secret and it is not meant to be: the list already says
 * whose world it is, next to the name.
 */
export function worldNameFor(owner: string): string {
  return `у-${encodeURIComponent(owner)}`;
}

/** One browser may host one world. Hosting two is hosting none, slowly. */
export const WORLDS_PER_HOST = 1;
/**
 * How many player-hosted worlds may be listed at once.
 *
 * Not about our load - each of these costs us a row in a Map and nothing
 * else, which is the point of the whole exercise. It is about the list
 * staying a list somebody can read.
 */
export const GUEST_WORLDS = 60;
/** People part-way through being introduced to one host, at once. */
export const WAITING_PER_WORLD = 8;
/**
 * The most an offer or an answer may be.
 *
 * A session description with a handful of candidates in it is two or three
 * kilobytes. Sixteen is room to spare; a megabyte is somebody using our
 * server as a post office.
 */
export const MAX_SDP = 16 * 1024;
/** One candidate is a line. */
export const MAX_CANDIDATE = 1024;
/**
 * How long an introduction is left standing.
 *
 * Nobody tells us when the two of them get through to each other - that is
 * the point - so a pairing is forgotten after a minute either way. A minute
 * is far longer than the handshake and far shorter than an evening.
 */
export const INTRODUCTION_SECONDS = 60;

/** A world somebody's browser is holding. */
export interface GuestWorld {
  readonly id: string;
  /** The client id of whoever is hosting it. */
  readonly by: number;
  readonly map: string;
  readonly name: Phrase;
  readonly blurb: Phrase;
  /** Whether the host will ask for a word. The word itself never reaches us. */
  readonly locked: boolean;
  /** Whoever is hosting, by name, so a list says whose world it is. */
  readonly owner: string;
  /** What the host last said. Their count, not ours - we cannot see in. */
  players: number;
  readonly since: number;
}

/** Somebody part-way through being introduced. */
interface Waiting {
  readonly guest: number;
  readonly world: string;
  readonly at: number;
}

export type Told<T> = { ok: true; it: T } | { ok: false; why: string };

const no = (why: string): { ok: false; why: string } => ({ ok: false, why });

export class Switchboard {
  private readonly worlds = new Map<string, GuestWorld>();
  /** Who is waiting on whom, by the waiting person's id. */
  private readonly waiting = new Map<number, Waiting>();

  /** Every world on somebody else's machine, for the list. */
  all(): readonly GuestWorld[] { return [...this.worlds.values()]; }

  get size(): number { return this.worlds.size; }

  hostOf(world: string): number | null {
    return this.worlds.get(world)?.by ?? null;
  }

  /** The world this client is holding, if it is holding one. */
  worldOf(host: number): string | null {
    for (const w of this.worlds.values()) if (w.by === host) return w.id;
    return null;
  }

  /**
   * "I am holding a world; tell people about it."
   *
   * The id is ours to give out, not theirs to choose: a host that named its
   * own world could name it `kremlin` and take the traffic meant for a world
   * on this machine.
   */
  open(
    by: number, map: string, name: Phrase, blurb: Phrase,
    owner: string, locked: boolean, now: number,
  ): Told<GuestWorld> {
    if (owner.length === 0) return no("sign-in");
    const mine = [...this.worlds.values()].filter((w) => w.by === by).length;
    if (mine >= WORLDS_PER_HOST) return no("your-worlds-full");
    /*
     * The same account, holding the same world from somewhere else.
     *
     * Two tabs, or a machine at work and one at home. Both are the same
     * world by name and only one of them has it: letting the second take
     * the row would point everybody at a copy that knows nothing of what
     * the first has been doing.
     */
    const id = worldNameFor(owner);
    if (this.worlds.has(id)) return no("your-worlds-full");
    if (this.worlds.size >= GUEST_WORLDS) return no("server-full");
    const world: GuestWorld = {
      id, by, map, name, blurb, locked, owner, players: 0, since: now,
    };
    this.worlds.set(world.id, world);
    return { ok: true, it: world };
  }

  /** How many are in there, according to the only one who can see. */
  said(by: number, world: string, players: number): void {
    const row = this.worlds.get(world);
    if (row === undefined || row.by !== by) return;
    row.players = Math.max(0, Math.min(999, Math.floor(players)));
  }

  /**
   * Their link has gone.
   *
   * Their world goes with it, and so does everybody waiting to be introduced
   * to it - a pairing with nobody on the far end is a row that never expires
   * on its own, because nothing will ever come back to clear it.
   */
  gone(who: number): readonly number[] {
    const stranded: number[] = [];
    for (const [id, world] of [...this.worlds]) {
      if (world.by !== who) continue;
      this.worlds.delete(id);
      for (const [guest, row] of [...this.waiting]) {
        if (row.world !== id) continue;
        this.waiting.delete(guest);
        stranded.push(guest);
      }
    }
    this.waiting.delete(who);
    return stranded;
  }

  /**
   * "Put me through to that world."
   *
   * Answers with whose link to send it down. The offer itself is not looked
   * at beyond its length.
   */
  offer(guest: number, world: string, sdp: string, now: number): Told<number> {
    if (sdp.length > MAX_SDP) return no("too-long");
    const row = this.worlds.get(world);
    if (row === undefined) return no("world");
    if (row.by === guest) return no("yourself");
    this.forget(now);
    const queue = [...this.waiting.values()]
      .filter((w) => w.world === world).length;
    if (queue >= WAITING_PER_WORLD) return no("busy");
    this.waiting.set(guest, { guest, world, at: now });
    return { ok: true, it: row.by };
  }

  /**
   * "Here is my answer for that person."
   *
   * Allowed only from the host of the world that person is waiting on. This
   * is the rule that stops anybody who guesses a number from answering a
   * stranger's offer and becoming their server.
   */
  answer(host: number, guest: number, sdp: string): Told<number> {
    if (sdp.length > MAX_SDP) return no("too-long");
    const row = this.waiting.get(guest);
    if (row === undefined) return no("nobody-waiting");
    if (this.hostOf(row.world) !== host) return no("not-yours");
    return { ok: true, it: guest };
  }

  /**
   * One more way of being reached, either way round.
   *
   * From a guest it needs no address - there is one host and they are
   * waiting on it. From a host it names which of the people waiting it is
   * for, and passes the same check the answer does.
   */
  candidate(
    from: number, to: number | null, line: string,
  ): Told<number> {
    if (line.length > MAX_CANDIDATE) return no("too-long");
    if (to === null) {
      const row = this.waiting.get(from);
      if (row === undefined) return no("nobody-waiting");
      const host = this.hostOf(row.world);
      return host === null ? no("world") : { ok: true, it: host };
    }
    return this.answer(from, to, "");
  }

  /** They got through, or gave up. Either way we are done with them. */
  through(guest: number): void { this.waiting.delete(guest); }

  /** Introductions nobody ever came back about. */
  forget(now: number): void {
    for (const [guest, row] of [...this.waiting]) {
      if (now - row.at > INTRODUCTION_SECONDS * 1000) this.waiting.delete(guest);
    }
  }

  /** For checks: who is waiting on what. */
  get pending(): number { return this.waiting.size; }
}
